import { useMemo } from "react";
import { Star, Gift, CheckCircle2, Clock, Sparkles, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useMyReview } from "@/hooks/useMyReview";
import { ReviewSubmissionCard } from "../ReviewSubmissionCard";

const STATUS_META = {
  pending: {
    label: "Pending review",
    icon: Clock,
    className: "bg-warning/15 text-warning border border-warning/30",
    description: "Your review is being checked by our team. It usually takes less than 24 hours.",
  },
  approved: {
    label: "Published",
    icon: CheckCircle2,
    className: "bg-success/15 text-success border border-success/30",
    description: "Your review is live on the website — thank you!",
  },
  rejected: {
    label: "Pending review",
    icon: Clock,
    className: "bg-warning/15 text-warning border border-warning/30",
    description: "Your review is being checked by our team. It usually takes less than 24 hours.",
  },
} as const;

export function ReviewSection() {
  const { review, loading, refresh } = useMyReview();

  const formattedDate = useMemo(() => {
    if (!review?.created_at) return null;
    try {
      return new Date(review.created_at).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return null;
    }
  }, [review?.created_at]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // ─── No review yet → CTA + submission card ────────────────────────────
  if (!review) {
    return (
      <div className="space-y-6">
        <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shadow-fluent-2 shrink-0">
              <Gift className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">
                  Get 15 free credits
                </h2>
                <Badge className="bg-primary text-primary-foreground hover:bg-primary/90">
                  +15 credits
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-1.5 max-w-xl">
                Share what Email2Contact does for you in a short review. Credits land on your
                account immediately after submitting — no waiting for approval.
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  ~1 minute
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-success" />
                  Instant credit reward
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Star className="h-3.5 w-3.5 text-warning fill-warning" />
                  Helps other professionals
                </span>
              </div>
            </div>
          </div>
        </div>

        <ReviewSubmissionCard onSubmitted={refresh} />
      </div>
    );
  }

  // ─── Review submitted → status card ───────────────────────────────────
  const meta = STATUS_META[review.status];
  const StatusIcon = meta.icon;

  return (
    <div className="space-y-5">
      {/* Status banner */}
      <div className={`rounded-xl p-4 flex items-start gap-3 ${meta.className}`}>
        <StatusIcon className="h-5 w-5 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="font-semibold text-sm">{meta.label}</p>
          <p className="text-sm opacity-90 mt-0.5">{meta.description}</p>
        </div>
      </div>

      {/* Review content */}
      <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
        <div className="p-5 sm:p-6 border-b border-border/60 bg-muted/20">
          <div className="flex items-start gap-4">
            {review.photo_url ? (
              <img
                src={review.photo_url}
                alt={review.display_name}
                className="h-14 w-14 rounded-full object-cover border-2 border-background shadow-fluent-2 shrink-0"
              />
            ) : (
              <div className="h-14 w-14 rounded-full bg-gradient-to-br from-primary to-primary-glow text-primary-foreground flex items-center justify-center font-semibold text-lg shadow-fluent-2 shrink-0">
                {review.display_name.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="font-semibold truncate">{review.display_name}</p>
              {review.display_role && (
                <p className="text-sm text-muted-foreground truncate">{review.display_role}</p>
              )}
              <div className="flex items-center gap-1 mt-1.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    key={n}
                    className={`h-4 w-4 ${
                      n <= review.rating
                        ? "fill-warning text-warning"
                        : "text-muted-foreground/30"
                    }`}
                  />
                ))}
                <span className="text-xs text-muted-foreground ml-2 tabular-nums">
                  {review.rating}/5
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-5 sm:p-6">
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{review.review_text}</p>
          {formattedDate && (
            <p className="text-xs text-muted-foreground mt-4 pt-4 border-t border-border/60">
              Submitted on {formattedDate}
            </p>
          )}
        </div>
      </div>

      {/* Reward confirmation */}
      <div className="rounded-xl border border-success/30 bg-success/5 p-4 flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg bg-success/15 text-success flex items-center justify-center shrink-0">
          <Gift className="h-4 w-4" />
        </div>
        <div className="text-sm">
          <p className="font-medium">15 credits awarded</p>
          <p className="text-muted-foreground text-xs mt-0.5">
            Reviews can only be submitted once and cannot be edited after submission.
          </p>
        </div>
      </div>
    </div>
  );
}
